import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';
import type { GuardrailEntry } from './guardrailsApi';
import type { TechStackEntry } from './techStackApi';
import type { CiFailure } from './fitnessApi';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface DuplicateMatch {
  id: string;
  title: string;
  type: 'guardrail' | 'techStack';
  explanation: string;
}

export interface StandardsAnalysis {
  summary: string;
  duplicates?: DuplicateMatch[];
  recommendations: {
    guardrails?: { entries: Omit<GuardrailEntry, 'id' | 'createdAt' | 'updatedAt'>[]; reason: string };
    techStack?: { entries: Omit<TechStackEntry, 'id' | 'createdAt'>[]; reason: string };
    claudeMd?: { suggestions: string[]; reason: string; regenerate: boolean };
  };
}

export const standardsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /**
   * `failures` carries real CI check output through to the analysis prompt, so a
   * guardrail proposed from a red cell is grounded in what the check printed
   * rather than in what the user remembered to retype.
   */
  async analyzeIssue(text: string, failures?: CiFailure[]): Promise<StandardsAnalysis> {
    const { data: job } = await api.post('/standards/analyze-issue', { text, failures });
    const jobId = job.jobId;

    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: status } = await api.get(`/standards/jobs/${jobId}`);
      if (status.status === 'completed') return status.result;
      if (status.status === 'failed') throw new Error(status.error || 'Analysis failed');
    }
    throw new Error('Analysis timed out');
  },
};
